/*
 *  This file auth-service.ts is part of HROne Inbox. The intelectual property owned by Uneecops Workplace Solution PVT. LTD.
 *  CopyrightYear: 2022
 *  (C) 2015-2022. All Right reserved. Uneecops Workplace Solution PVT. LTD.
 *
 */

import { HttpClient, HttpHeaders } from "@angular/common/http";
import { DestroyRef, effect, EventEmitter, inject, Injectable, signal } from "@angular/core";
import { Router } from "@angular/router";
import moment from "moment";
import { EMPTY, forkJoin, Observable, of, Subject, throwError } from "rxjs";
import { catchError, concatMap, first, map, switchMap, tap } from "rxjs/operators";
import { SingleSignonService } from "src/app/authentication/single-signon.service";
import { TCoreMenuAccessTeamActionsForPin } from "src/app/main/sidebar/sidebar.model";
import { EnterpriseWallService } from "src/app/modules/enterprise-wall/enterprise-wall.service";
import { IHroneGenericResponse } from "src/app/modules/enterprise-wall/social-enterpise-wall/social-wall.model";
import { refreshPageAccessRightSuccess } from "src/app/store/global/global.action";
import { EGlobalStateProcessId, EGlobalStateStoreNames, GLOBAL_API_URLS, TCoreMenuAccessPayload, TCoreMenuAccessResponse, TPageAccessRightStoreData, TWorkforceSetupSettingResponse } from "src/app/store/global/global.constants";
import { TGlobalStateProcessVersion } from "src/app/store/global/global.reducer";
import { environment } from "src/environments/environment";
import { menuNavigationModel } from "../constants/routing-constant";
import { LogonUserDetailModel, TLoginTokenResponse } from "../models/common.models";
import { CustomRequestMenuModel, RequestMenuModel } from "../models/request-menu.model";
import { ConfigurationService } from "./configuration.service";
import { CommonDropdownService } from "./dropdowns/common-dropdown.service";
import { LocationDropdownsService } from "./dropdowns/location-dropdowns.service";
import { OsDropdownsService } from "./dropdowns/os-dropdowns.service";
import { ServerErrorHandler } from "./error-handler/server-error-handler.service";
import { HttpService } from "./http-service";
import { IUserLicenseInfo, SharedService } from "./shared.service";
import { SnackbarAlertService } from "./snackbar-alert.service";
import { UserRightsService } from "./user-rights.service";
import { VwoService } from "./vwo.service";


@Injectable()
export class AuthService {
  onloggedInEvent = new EventEmitter();
  onCloseDialogEvent = new EventEmitter();
  bpUserEvent = new EventEmitter();
  otpResendEvent = new Subject()
  onLogout$ = new Subject();
  isTimerStarted = false;
  base_auth_url!: string;
  domainCode;
  base_url;
  employeeInfoAfterLogin: LogonUserDetailModel | null = null;
  superUserInfoAfterLogin = null;
  newAddedMenu: TCoreMenuAccessResponse[] = [];
  pageName = '';
  currentOption: { Id: number, detail: string, type: string, isUsed: boolean } | null = null;
  onNextClicked!: boolean;
  isRefreshToken = false;
  resetPass = signal<number>(0);
  username = signal<string>('')

  constructor(
    private readonly _httpClient: HttpClient,
    public _router: Router,
    public _serverError: ServerErrorHandler,
    public _config: ConfigurationService,
    public _wall: EnterpriseWallService,
    private readonly _shared: SharedService,
    private readonly http: HttpService,
    public _common: CommonDropdownService,
    public _location: LocationDropdownsService,
    public _osdd: OsDropdownsService,
    public _userRights: UserRightsService,
    public _singleSignon: SingleSignonService,
    private readonly _snackBar: SnackbarAlertService,
    public readonly _vwoService: VwoService
  ) {
    const destroyRef = inject(DestroyRef);
    this.domainCode = localStorage.getItem('domainCode') ? localStorage.getItem('domainCode') : _config.domainCode;
    this.base_url = _config["base_url"];
    this.base_auth_url = _config["base_auth_url"];
    const newAddedMenuRaw = localStorage.getItem('newAddedMenu');
    const newAddedMneuRaw = localStorage.getItem('newAddedMneu');
    if (newAddedMenuRaw) {
      this.newAddedMenu = JSON.parse(newAddedMenuRaw);
    } else if (newAddedMneuRaw) {
      this.newAddedMenu = JSON.parse(newAddedMneuRaw);
    } else {
      this.newAddedMenu = [];
    }
    const stopEffect = effect(() => {
      const val = this._serverError.refreshMenuSignal();
      if (val !== null) {
        this.getOtherInfo().then(() => {
          location.reload();
        })
      }
    })
    destroyRef.onDestroy(() => stopEffect.destroy());
  }

  getHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/json",
      domainCode: this.domainCode!,
      AccessMode: 'W'
    });
  }

  getLoginHeaders(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      domainCode: this.domainCode!,
      AccessMode: 'W'
    });
  }

  getLoginHeadersWithRefreshHeader(): HttpHeaders {
    return new HttpHeaders({
      "Content-Type": "application/x-www-form-urlencoded",
      domainCode: this.domainCode!,
      AccessMode: 'W',
      'hrone-refresh-header': 'true'
    });
  }

  public Get<T = any>(url: string): Observable<T> {
    return this._httpClient
      .get<T>(this.base_url + url, { headers: this.getHeaders() })
      .pipe(catchError(err => this._serverError.handleServerError(err)));
  }

  public Post<T = any, P = any>(url: string, param: P): Observable<T> {
    return this._httpClient
      .post<T>(this.base_url + url, param, { headers: this.getHeaders() })
      .pipe(catchError(err => this._serverError.handleServerError(err)));
  }

  public PostUnAuthorize(url: string, param: any): Observable<any> {
    const headers = this.getHeaders();
    return this._httpClient
      .post(this.base_auth_url + url, param, { headers: headers })
      .pipe(catchError(err => this._serverError.handleServerError(err, 'isUnauthorize')));
  }

  public LoginUnAuthorized(url: string, param: any, withCredentials = true): Observable<any> {
    let headers = this.getLoginHeaders();
    if (!withCredentials) {
      headers = headers.set('skip-withcredentials', 'true');
    }
    return this._httpClient
      .post(this.base_auth_url + url, param, { headers: headers, withCredentials })
      .pipe(catchError(err => this._serverError.handleServerError(err, 'isUnauthorize')));
  }

  public LoginUnAuthorizedWithRefreshHeader(url: string, param: any, withCredentials = true): Observable<any> {
    let headers = this.getLoginHeadersWithRefreshHeader();
    if (!withCredentials) {
      headers = headers.set('skip-withcredentials', 'true');
    }
    return this._httpClient
      .post(this.base_auth_url + url, param, { headers: headers, withCredentials })
      .pipe(catchError(err => this._serverError.handleServerError(err, 'isUnauthorize')));
  }

  encodedPost(url: string, param: any) {
    const headers = this.getLoginHeaders();
    return this._httpClient.post(this.base_auth_url + url, param, { headers: headers })
      .pipe(catchError(err => this._serverError.handleServerError(err, 'isUnauthorize')));
  }

  setLoginAuth(data: any) {
    localStorage.setItem("authlogin", JSON.stringify(data));
    this._shared.getTokenInfo();
  }



  isAuthenticated() {
    if (localStorage.getItem("authlogin")) {
      return true;
    }
    this.http.updateSignalRAuthTokenSubject.next({ token: null, isLogout: true });
    return false;
  }

  isAuthenticatedOrSSO(): boolean {
    const token: TLoginTokenResponse | null = JSON.parse(localStorage.getItem("authlogin") ?? 'null');
    this.isRefreshToken = !!(token && (token?.refresh_token || "").toLowerCase() !== 'na' && !this.onNextClicked)
    return this.isRefreshToken;
  }


  isAccessTokenExpired(): boolean {
    if (JSON.parse(localStorage.getItem("isRefreshingToken") ?? 'null')) return false;
    const gmtNow = new Date().toUTCString();
    const token: TLoginTokenResponse | null = JSON.parse(localStorage.getItem("authlogin") ?? 'null');
    if ((token?.refresh_token || "").toLowerCase() === 'na') return false;
    const howManyUnitsAheadFromExpiry = moment(token?.['.expires']).diff(moment(gmtNow), 'seconds');
    return token === null || howManyUnitsAheadFromExpiry <= token.expires_in / 2;
  }


  logout() {
    this.isRefreshToken = false;
    try {
      if (localStorage.getItem('authlogin')) {
        const authlogin = JSON.parse(localStorage.getItem("authlogin") ?? 'null');
        const accessToken = authlogin ? authlogin.access_token : "";
        this.logOutFromWeb(accessToken);
      }
      this.clearLocalStorage();
      if (environment.redirectToMicro) {
        window.location.href = environment.loginRedirectUrl
      } else {
        this._router.navigate(['/login']);
      }
    } catch (err) { }


    try {
      this._wall.resetAllData();
      this._common.reset();
      this._location.reset();
      this._osdd.reset();
    } catch (err) { }



    this._shared.reset();
    this.onLogout$.next(Math.random());
  }

  clearLocalStorage() {
    localStorage.removeItem('authlogin');
    localStorage.removeItem('newAddedMneu');
    localStorage.removeItem('rawMenusWithActions');
    localStorage.removeItem('socketId');
    localStorage.removeItem('rawMenus');
    localStorage.removeItem('logOnUserDetails');
    localStorage.removeItem('whiteLabels');
    localStorage.removeItem('uid');
    localStorage.removeItem('GC_Accesstoken');
    localStorage.removeItem('requestSource');
    localStorage.removeItem('selectedHRDeskSearch');
    if (this.employeeInfoAfterLogin?.employeeStatusId == 7) {
      localStorage.removeItem('recent_enterprise_search');
    }
    localStorage.removeItem('samlSessionIndex');
    localStorage.removeItem('contactDialogShown');
    localStorage.removeItem('newAddedMenu');
    localStorage.removeItem('notificationFetched');
    localStorage.removeItem('hasSeenMobileDialog');
    localStorage.removeItem('recent_report_searches');
    localStorage.removeItem('recent_report_searches_team');
    localStorage.removeItem('cart');

    // please change the same function in hrone-network service as well.
  }

  logOutFromWeb(accessToken: string) {
    if (accessToken) {
      try {
        const headers = new HttpHeaders({
          "Content-Type": "application/json",
          domainCode: this.domainCode!,
          AccessMode: 'W'
        });

        this._httpClient
          .get(this.base_url + "/api/LogOnUser/LogOff", {
            headers: headers
          })
          .subscribe(res => {
            localStorage.removeItem('launchDialog');
            this._shared.hrHanbookList = [];
            this._shared.hrDesk_Import_Page_List = [];
            this._shared.hrDesk_Action_List = [];
            this._shared.hrDesk_Available_Searches_List = [];
          });
      } catch (err) { }
    }
  }



  getLoginInfo(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      this.Get('/api/LogOnUser/LogOnUserDetail').subscribe({
        next: (res) => this.handleLogOnUserDetailResponse(res, resolve, reject),
        error: () => reject(new Error('false'))
      });
    });
  }

  private handleLogOnUserDetailResponse(
    res: any,
    resolve: (value: boolean) => void,
    reject: (value: boolean) => void
  ): void {
    if (!res) {
      resolve(false);
      return;
    }
    if (res.isLoggedOn < 2) this.updateLogOnStatus();
    this.employeeInfoAfterLogin = res;
    localStorage.setItem('logOnUserDetails', JSON.stringify(res));
    localStorage.setItem('isEmailBlocked', JSON.stringify(res?.isEmailBlocked));
    this._shared.logOnUserDetails = res;
    if (this._shared.logOnUserDetails?.identifier) {
      this._shared.logOnUserIdentifiers = this._shared.logOnUserDetails.identifier;
    }
    this.getOnboardingStatus().subscribe({
      next: (settingRes) => this.handleOnboardingStatusResponse(settingRes, resolve, reject),
      error: () => this.resolveViaGetOtherInfo(resolve, reject)
    });
  }

  private handleOnboardingStatusResponse(
    settingRes: any,
    resolve: (value: boolean) => void,
    reject: (value: boolean) => void
  ): void {
    if (settingRes?.status !== 'Y') {
      this.resolveViaGetOtherInfo(resolve, reject);
      return;
    }
    this.isProcessStart().subscribe({
      next: (onboardingRes) => this.handleProcessStartResponse(onboardingRes, resolve, reject),
      error: () => this.resolveViaGetOtherInfo(resolve, reject)
    });
  }

  private handleProcessStartResponse(
    onboardingRes: any,
    resolve: (value: boolean) => void,
    reject: (value: boolean) => void
  ): void {
    this._shared.onboardingAccess = true;
    if (!onboardingRes.isProcessStart) {
      this.getOtherInfo()
        .then((res: boolean) => {
          this._shared.userLoggedOut.next(res);
          resolve(res);
        })
        .catch(() => reject(false));
      return;
    }
    const pageNumber: number = onboardingRes?.selectedOnPageNumber ? onboardingRes?.selectedOnPageNumber : 1;
    localStorage.setItem('isProcessStart', JSON.stringify(true));
    localStorage.setItem('onboardingPageInfo', JSON.stringify({
      segmentTypeId: onboardingRes?.selectedSegmentTypeId,
      pageNumber
    }));
    if (onboardingRes?.selectedSegmentTypeId) {
      this._router.navigate(['/onboarding-config', pageNumber, onboardingRes?.selectedSegmentTypeId]);
    } else {
      this._router.navigate(['/onboarding-config', pageNumber]);
    }
    this.getOtherInfo()
      .then(() => resolve(false))
      .catch(() => reject(false));
  }

  private resolveViaGetOtherInfo(
    resolve: (value: boolean) => void,
    reject: (value: boolean) => void
  ): void {
    this.getOtherInfo()
      .then((res: boolean) => {
        this._shared.userLoggedOut.next(res);
        resolve(res);
      })
      .catch(() => reject(false));
  }

  getOnboardingStatus(): Observable<TWorkforceSetupSettingResponse | null> {
    return this._shared._globalState.getVersionDetails().pipe(
      switchMap((data) => {
        this._shared._globalState.handleVersionDetails(data!);
        return this._shared._globalState.getWorkforceSetupSetting(68);
      }),
      catchError(() => this._shared._globalState.getWorkforceSetupSetting(68))
    );
  }

  isProcessStart() {
    const onboardingURL = '/api/core/onboard/CheckProcess';
    return this.http.Get(onboardingURL)
  }

  updateLogOnStatus() {
    const url = '/api/workforce/Employee/UpdateLogOnStatus';
    this.http.Put(url, {}).subscribe(res => res)
  }
  getOtherInfo(): Promise<boolean> {
    return new Promise((resolve, reject) => {
      const { forkedMenus, userLicenceInfo } = this.buildMenuObservables();
      this.getLegoFeatureLicense()
        .then((isLegoLicenseEnabled) => {
          if (isLegoLicenseEnabled) {
            this.getLegoOrgId().pipe(first()).subscribe();
          }
          forkJoin({ forkedMenus, userLicenceInfo, formMenuList: of<LegoFormItem[]>([]) }).pipe(
            first(),
            tap((data) => this.processForkJoinMenuData(data, isLegoLicenseEnabled, resolve)),
            catchError(() => {
              resolve(false);
              return EMPTY;
            })
          ).toPromise().then(() => { }).catch(() => reject(new Error('false')));
        })
        .catch(() => {
          localStorage.setItem('hasCustomMenus', JSON.stringify(false));
          reject(new Error('false'));
        });
    });
  }

  private buildMenuObservables(): {
    forkedMenus: Observable<{ menuAction: TCoreMenuAccessTeamActionsForPin; menuList: TCoreMenuAccessResponse[]; menuAccess: TCoreMenuAccessResponse[] }>;
    userLicenceInfo: Observable<IUserLicenseInfo>;
  } {
    const MenuModelObject: TCoreMenuAccessPayload = { menuType: 'P', parentKey: 9999, menuId: 0, includeMenu: true };
    const MenuModelObject2: TCoreMenuAccessPayload = { menuType: 'A', parentKey: 9999, menuId: 0, includeMenu: true };
    const menuAction$ = forkJoin({
      menuAction: this.Get<TCoreMenuAccessTeamActionsForPin>(GLOBAL_API_URLS[EGlobalStateProcessId.PageAccessRights].MENU_ACCESS),
      menuList: this.Post<TCoreMenuAccessResponse[], TCoreMenuAccessPayload>(GLOBAL_API_URLS[EGlobalStateProcessId.PageAccessRights].MENU_ACCESS, MenuModelObject),
      menuAccess: this.Post<TCoreMenuAccessResponse[], TCoreMenuAccessPayload>(GLOBAL_API_URLS[EGlobalStateProcessId.PageAccessRights].MENU_ACCESS, MenuModelObject2)
    }).pipe(
      map((data) => {
        this._shared._globalState.store.dispatch(refreshPageAccessRightSuccess({
          data: { menuAccess: data.menuAccess, menuAction: data.menuAction, menuList: data.menuList, refresh: false },
          domainCode: this._shared._globalState.logOnUserDetails().domainCode,
          employeeId: this._shared._globalState.logOnUserDetails().employeeId,
          storename: EGlobalStateStoreNames.PageAccessRights,
          versionId: this._shared._globalState.versionhash.get(EGlobalStateProcessId.PageAccessRights) ?? '1',
        }));
        return (data ? structuredClone(data) : data) as { menuAction: TCoreMenuAccessTeamActionsForPin; menuList: TCoreMenuAccessResponse[]; menuAccess: TCoreMenuAccessResponse[] };
      }),
      catchError(() => EMPTY)
    );
    const forkedMenus = this._shared._globalState.indexdb.getByKey<TGlobalStateProcessVersion<{ data: TPageAccessRightStoreData; loading: boolean }>>('hronestore', EGlobalStateProcessId.PageAccessRights).pipe(
      map((dbData: { [key: string]: any }) => ({
        expired: dbData && 'data' in dbData && dbData.data.menuAccess && this._shared._globalState.shouldGetFromCache(dbData.versionId, EGlobalStateProcessId.PageAccessRights, dbData.employeeId, dbData.domainCode, dbData.date),
        data: dbData
      })),
      concatMap((cacheExpired: any) => {
        const dbData = cacheExpired.data;
        if (dbData && dbData.data.menuAccess && dbData.data.menuAction && dbData.data.menuList &&
          this._shared._globalState.shouldGetFromCache(dbData.versionId, EGlobalStateProcessId.PageAccessRights, dbData.employeeId, dbData.domainCode, dbData.date)) {
          return of({ menuAccess: dbData.data.menuAccess, menuAction: dbData.data.menuAction, menuList: dbData.data.menuList }) as Observable<{ menuAction: TCoreMenuAccessTeamActionsForPin; menuList: TCoreMenuAccessResponse[]; menuAccess: TCoreMenuAccessResponse[] }>;
        }
        return menuAction$;
      }),
      catchError(() => menuAction$)
    );
    const userLicenceInfo = this.Get<IUserLicenseInfo>('/api/UserLicense/info');
    return { forkedMenus, userLicenceInfo };
  }

  private getFormMenuListObservable(): Observable<LegoFormItem[] | null> {
    const orgId = this._shared.legoOrgId ?? this._config.legoOrgId;
    return this.http.GetLegoService(`/api/organisations/${orgId}/forms?limit=1000`).pipe(
      map((response: LegoFormsApiResponse) => response?.data ?? []),
      // null (not []) signals a failed fetch so callers can keep the last-known-good list instead of clearing it
      catchError(() => of(null))
    );
  }

  private processForkJoinMenuData(data: any, isLegoLicenseEnabled: boolean, resolve: (value: boolean) => void): void {
    this.processMenuActionRights(data.forkedMenus.menuAction);
    this.processMenuListData(data.forkedMenus.menuList, data.formMenuList ?? [], isLegoLicenseEnabled);
    this.processMenuAccess(data.forkedMenus.menuAccess);
    if (data.userLicenceInfo) {
      this._shared.userLicenseInfo = data.userLicenceInfo;
    }
    resolve(true);
  }

  private processMenuActionRights(action: any): void {
    if (!action?.menuPermissionRights?.length) return;
    action.menuPermissionRights = action.menuPermissionRights.map((menu: any) => {
      menu.navigationCode = (menu.navigationCode || '').toLowerCase();
      return menu;
    });
    localStorage.setItem('rawMenusWithActions', JSON.stringify(action.menuPermissionRights));
    this._shared.rawMenusWithActions = action.menuPermissionRights;
    this._userRights.rawMenusWithActions = action.menuPermissionRights;
  }

  private processMenuListData(menuList: any[], formMenuItems: LegoFormItem[], isLegoLicenseEnabled: boolean): void {
    if (!menuList?.length) return;
    let processedList = menuList.map((menu: any) => {
      menu.navigationCode = (menu.navigationCode || '').toLowerCase();
      return menu;
    });
    const rawMenus = processedList;
    try {
      const found = rawMenus.find((e: any) => e.menuId === 64);
      if (found) found.sequenceNumber = 6;
    } catch (e) { }
    if (isLegoLicenseEnabled) {
      this.addDynamicFormMenusToRaw(rawMenus, formMenuItems);
    }
    localStorage.setItem('hasCustomMenus', JSON.stringify(isLegoLicenseEnabled));
    this._shared.bindNewBetaOnMenu(processedList);
    const rawMenusNew = RequestMenuModel.concat(rawMenus as unknown as typeof RequestMenuModel);
    this.applyNavigationPathsToRawMenus(rawMenusNew);
    localStorage.setItem('rawMenus', JSON.stringify(rawMenusNew));
    this._shared.rawMenus = rawMenusNew as any[];
    this._shared.refreshRecentSearchedProfileData();
  }

  private buildDynamicFormMenuItems(formMenuItems: LegoFormItem[]): any[] {
    return formMenuItems.map((form: LegoFormItem, index: number) => {
      const formIdHash = parseInt(form.id.substring(form.id.length - 8), 16) || (Math.abs(form.id.split('').reduce((a: number, b: string) => ((a << 5) - a) + b.charCodeAt(0), 0)));
      return {
        activeStatus: "1", controlKey: null, cssClassName: form.name.replace(/[^a-zA-Z0-9]/g, '_'),
        iconImagePath: "Icon", includeMenu: true, isPin: false,
        menuId: formIdHash, menuName: form.name, menuType: "P", moduleId: 4,
        navigationCode: `custom_${form.id}`, navigationPath: `/app/request/initiate-custom-request/${form.id}`,
        parentKey: 64, sequenceNumber: 1000 + index, status: false, sub_menu: [],
        tooltipText: form.name, formId: form.id, appId: form.appId, isNewBeta: "", hidePinUnpin: true
      };
    });
  }

  private addDynamicFormMenusToRaw(rawMenus: any[], formMenuItems: LegoFormItem[]): void {
    this.buildDynamicFormMenuItems(formMenuItems).forEach(item => rawMenus.push(item));
    CustomRequestMenuModel.forEach((statusMenu: any) => rawMenus.push({ ...statusMenu } as any));
  }

  private applyNavigationPathsToRawMenus(rawMenusNew: any[]): void {
    menuNavigationModel.forEach((item: any) => {
      try {
        const itemFound = rawMenusNew.find((e: any) => e.navigationCode === item.code);
        if (itemFound && !itemFound.navigationCode.startsWith('custom_')) {
          itemFound.navigationPath = item.path;
        }
      } catch (e) { }
    });
  }

  private processMenuAccess(menuAccess: any[]): void {
    if (!menuAccess?.length) return;
    menuAccess.forEach((menu: any) => {
      menu.navigationCode = (menu.navigationCode || '').toLowerCase();
    });
    this.newAddedMenu = menuAccess;
    localStorage.setItem('newAddedMenu', JSON.stringify(menuAccess));
  }

  getRefreshToken(): Observable<TLoginTokenResponse> {
    const authLoginStr = localStorage.getItem("authlogin");
    const logOnUserDetailsStr = localStorage.getItem('logOnUserDetails');
    const authlogin = JSON.parse(authLoginStr ?? JSON.stringify({}));
    const url = "/oauth2/token";
    const logOnUserDetails = JSON.parse(logOnUserDetailsStr ?? JSON.stringify({}));
    const username = localStorage.getItem('logOnUserName') ? localStorage.getItem('logOnUserName') : logOnUserDetails ? logOnUserDetails.officialEmail : '';
    const domainCode = localStorage.getItem('domainCode') ? localStorage.getItem('domainCode') : this._config.domainCode;
    const body = `refreshId=${authlogin ? authlogin.refresh_token : ''}&companyDomainCode=${domainCode ?? ''}&grant_type=refresh_token&username=${username || ''}`;
    return this.LoginUnAuthorized(url, body);
  }

  getRefreshTokenWithRefreshHeader(): Observable<TLoginTokenResponse> {
    const authLoginStr = localStorage.getItem("authlogin");
    const logOnUserDetailsStr = localStorage.getItem('logOnUserDetails');
    const authlogin = JSON.parse(authLoginStr ?? JSON.stringify({}));
    const url = "/oauth2/token";
    const logOnUserDetails = JSON.parse(logOnUserDetailsStr ?? JSON.stringify({}));
    const storedUserName = localStorage.getItem('logOnUserName');
    let username: string;
    if (storedUserName) {
      username = storedUserName;
    } else if (logOnUserDetails?.officialEmail) {
      username = logOnUserDetails.officialEmail;
    } else {
      username = '';
    }
    if (!authlogin || (authlogin.refresh_token == undefined || authlogin.refresh_token == null)) {
      return throwError(() => ({ status: 400, message: 'Refresh token not found' }));
    }
    const domainCode = localStorage.getItem('domainCode') ? localStorage.getItem('domainCode') : this._config.domainCode;
    const body = `refreshId=${authlogin ? authlogin.refresh_token : ''}&companyDomainCode=${domainCode ?? ''}&grant_type=refresh_token&username=${username || ''}`;
    return this.LoginUnAuthorizedWithRefreshHeader(url, body);
  }
  callsAfterLogin(): void {
    const url = '/api/LogOnUser/AuditTrail';
    const reqData = {
      "logOnSource": "W",
      "internetProtocolAddress": '',
      "remarks": ""
    };
    const newBetaJSON = this._httpClient.get('/assets/json/new-beta.json');
    const commonLoginEvent = this.Get('/api/engage/employee/CommonLoginEvent/' + this._shared.logOnUserDetails.employeeId).pipe(catchError(err => of([])));
    const sendIpAddress = this.Post(url, reqData).pipe(catchError(err => of(null)));
    const whiteLabels = this.Get('/api/core/WhiteLabeling').pipe(catchError(err => of([])));
    const storedCustomerCode = localStorage.getItem('customerCode');
    const customerCode = storedCustomerCode ? of({ customerCode: storedCustomerCode }) : this.Get('/api/core/Dashboard/SoftwareLicense/CustomerCode').pipe(catchError(err => of(null)));

    forkJoin([newBetaJSON, commonLoginEvent, sendIpAddress, whiteLabels, customerCode]).pipe(first(), tap(data => {



      this._shared.betaValue = data[0];
      if (data[1]?.length) {
        this._shared.commonLoginPopupData = data[1];
        this._shared.pulseFeedbackArr = data[1].filter((e: { serviceEventId: number }) => e.serviceEventId === 2);
        this._shared.getPulseData(this._shared.pulseFeedbackArr[0]);
      }
      if (data[3]?.length) {
        localStorage.setItem('whiteLabels', JSON.stringify(data[3]));
        data[3].forEach((ele: { [key: string]: any }) => {
          const prop = (ele.originalDisplayName)?.toLowerCase();
          this._shared.whiteLabels[prop] = ele.displayName;
        });
      }
      if (data[4]?.customerCode) {
        localStorage.setItem('customerCode', data[4].customerCode);
        this._vwoService?.setVisitorAttribute(this._shared.logOnUserDetails.employeeId + '~' + data[4].customerCode);
      }

    })).subscribe();
  }

  logOutFromSingleSignOn(logOnTypeDetails: any, logOnUserDetails: LogonUserDetailModel, samlSessionIndex: string | null): void {
    if (logOnTypeDetails?.isSingleSignOnEnable && logOnTypeDetails?.isActiveUser && logOnTypeDetails?.logOnSetting) {
      if (logOnTypeDetails.logOnSetting.logOnType == 5) {
        if (logOnTypeDetails.logOnSetting.subLogOnType == 3) {
          const header = new HttpHeaders({ 'Content-Type': 'application/json' });
          this._httpClient.delete(logOnTypeDetails.logOnSetting.logOffPath, { headers: header, withCredentials: true }).subscribe();
        } else if (samlSessionIndex) {
          const headers = new HttpHeaders({
            'Content-Type': 'application/x-www-form-urlencoded',
            AccessMode: 'W',
            domainCode: localStorage.getItem('domainCode') ?? "",
          });
          const body = new URLSearchParams({
            logOnUserId: logOnUserDetails.employeeId.toString(),
            domainCode: logOnUserDetails.domainCode,
            sessionIndex: samlSessionIndex
          }).toString();
          this._httpClient.post<IHroneGenericResponse>(this.http._config.base_auth_url + '/api/saml/request/logout', body, { headers })
            .subscribe((res: IHroneGenericResponse) => {
              if (res?.validationType == '0') {
                this._singleSignon.onSignIn(res.messageCode);
              }
            });
        }
      }
    }
  }

  logInSamlO365(username: string): void {
    const headers = new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      AccessMode: 'W',
      domainCode: localStorage.getItem('domainCode') ?? "",
    });
    const body = new URLSearchParams({
      logOnParam: username
    }).toString();
    this._httpClient.post<IHroneGenericResponse>(this.http._config.base_auth_url + '/api/saml/request/login', body, { headers }).subscribe(
      (res: IHroneGenericResponse) => {
        if (res?.validationType == '0') {
          this._singleSignon.onSignIn(res.messageCode);
        } else {
          this._snackBar.Alert(res.message, res.validationType);
        }
      });
  }

  redirectAfterToken(redirect_url: string, target: '_self' | '_blank', isAccessModeRequired = false): void {
    this.getRefreshToken().subscribe({
      next: (auth) => {
        const url = this.generateRedirectURL(redirect_url, auth, isAccessModeRequired);
        this.setLoginAuth(auth);
        window.open(url, target);
      },
      error: () => {
        sessionStorage.setItem('redirect_url', redirect_url);
        this.logout();
      }
    });
  }

  generateRedirectURL(redirect_url: string, auth: TLoginTokenResponse, isAccessModeRequired = false): string {
    const url = isAccessModeRequired ? `${redirect_url}?access_token=${auth.access_token}&auth=${btoa(this._config.base_auth_url)}&AccessMode=W&refresh_token=${auth.refresh_token}` : `${redirect_url}?access_token=${auth.access_token}&auth=${btoa(this._config.base_auth_url)}&refresh_token=${auth.refresh_token}`;
    return url;
  }
  generateRedirectURLReports(redirect_url: string, auth: TLoginTokenResponse, isAccessModeRequired: boolean, reportname: string, url: string): string {
    const urlWithParams = `${redirect_url}?reportname=${encodeURIComponent(reportname)}&url=${encodeURIComponent(url)}`;
    return isAccessModeRequired ? `${urlWithParams}&access_token=${auth.access_token}&auth=${btoa(this._config.base_auth_url)}&AccessMode=W&refresh_token=${auth.refresh_token}` : `${urlWithParams}&access_token=${auth.access_token}&auth=${btoa(this._config.base_auth_url)}&refresh_token=${auth.refresh_token}`;
  }
  downloadHandoverSheet(body: { Month: number, Year: number }): Observable<{ fileDbName: string; fileVirtualPath: string; fileName: string; initiatedId: number; folderName: string }> {
    return this.Post('/api/core/report/OnboardingStatus', body);
  }

  getLegoFeatureLicense(): Promise<boolean> {
    return this._shared.isFeatureAvailable(267).then((response: boolean) => {
      localStorage.setItem('isLegoFeatureLicenseEnabled', JSON.stringify(response));
      return response;
    }).catch(() => {
      localStorage.setItem('isLegoFeatureLicenseEnabled', JSON.stringify(false));
      return false;
    });
  }

  getLegoOrgId(): Observable<string> {
    return this._shared._globalState.getLegoOrgId().pipe(
      tap((orgId: string) => {
        if (orgId) {
          this._config.legoOrgId = orgId;
          localStorage.setItem('legoOrgId', orgId);
        }
      }),
      map((orgId: string) => orgId || this._config.legoOrgId),
      catchError(() => {
        return of(this._config.legoOrgId);
      })
    );
  }

  /** On-demand Lego forms fetch, triggered when the Request tab is opened (not at login). Null means the fetch failed. */
  fetchLegoFormMenuItems(): Observable<LegoFormItem[] | null> {
    return this.getFormMenuListObservable();
  }

  /** Merges freshly-fetched Lego form menu items into rawMenus, replacing any items from a previous fetch. Returns the new items so the sidebar can merge them into its already-built menu tree. */
  mergeFormMenuItemsIntoRawMenus(formMenuItems: LegoFormItem[]): any[] {
    const dynamicItems = this.buildDynamicFormMenuItems(formMenuItems);
    const rawMenus = (this._shared.rawMenus ?? []).filter((m: any) => !m.formId).concat(dynamicItems);
    localStorage.setItem('rawMenus', JSON.stringify(rawMenus));
    this._shared.rawMenus = rawMenus as any[];
    return dynamicItems;
  }
}

// Lego Forms API Response Types
export interface LegoFormItem {
  name: string;
  appId: string;
  id: string;
}

export interface LegoFormsApiResponse {
  data: LegoFormItem[];
  page: {
    limit: number;
    total: number;
    offset: number;
    next: number;
  };
}